import { defineStore } from 'pinia';
import DataRecordsRepository from "@/Repositories/DataGathering/DataRecordsRepository";
import _ from "lodash";
import {EventBus} from "@/event-bus";

export const DataRecordsStore = defineStore('DataRecordsStore', {
    state: () => ({
        dataGathering: {},

        dataRecords: [],

        pagination: {
            max: 10,
            page: 1,
            pages: null,
            total: null,
            visible_pages: 10,
        },

        currentDataRecord: {},
        currentDataRecordIndex: null,

        filters: {},

        sortBy: [],

        loading: false,
    }),

    getters: {
        /**
         * Returns all the available columns for the data-records
         *
         * @returns {any[]}
         */
        columns() {
            //main column configuration coming from the data-type relationship
            const columnConfigurations = _.get(this.dataGathering, 'type.options.columns', []);
            //custom column configuration coming from data-gathering itself
            const customConfigurations = _.get(this.dataGathering, 'options.columns', []);

            //mix main and custom configurations
            return columnConfigurations.map(
                column => _.merge(
                    {},
                    column,
                    customConfigurations.firstOf(customColumn => customColumn.id === column.id) || {},
                )
            );
        },

        visibleColumns(){
            if (!Array.isArray(this.columns))
                return [];

            return this.columns.filter(column => column.show) || [];
        },

        /**
         *
         * Return the headers with the property show_more_editable
         *
         * @returns {*[]}
         */
        showMoreColumns(){
            if (!Array.isArray(this.columns))
                return [];

            return this.columns.filter(column => column.show_more_editable) || [];
        },

        /**
         *
         * Return the headers with the property show_less_editable
         *
         * @returns {*[]}
         */
        showLessColumns(){
            if (!Array.isArray(this.columns))
                return [];

            return this.columns.filter(column => column.show_less_editable) || [];
        },

        showSteps() {
          if (!Array.isArray(this.columns))
              return [];

          return this.columns.filter(column => column.step_index === 1) || [];
        },

        /**
         * Return the available headers for the data-table
         *
         * @returns {any[]}
         */
        dataTableHeaders() {
            //assign default values for the data-table itself (if needed)
            let headers = this.visibleColumns.map(column => _.merge( {
                align: _.get(column, 'align', 'left'),
                text: (_.get(column, 'show_short_title') ? _.get(column, 'short_title') : _.get(column, 'title')),
                title: _.get(column, 'title'),
                value: _.get(column, 'value', column.id),
                sortable: _.get(column, 'sortable', false),
            }, column));

            headers.push({
                value: 'actions',
                sortable: false
            }, {
                value: 'data-table-expand',
                sortable: false
            });

            return headers;
        },

        /**
         *
         * Return the available headers for show more of the data-record-edit-form
         *
         * @returns {*}
         */
        showMoreEditHeaders() {
            //assign default values for the data-table itself (if needed)
            let headers = this.showMoreColumns.map(column => _.merge( {
                align: _.get(column, 'align', 'left'),
                text: (_.get(column, 'show_short_title') ? _.get(column, 'short_title') : _.get(column, 'title')),
                title: _.get(column, 'title'),
                value: _.get(column, 'value', column.id),
                sortable: _.get(column, 'sortable', false),
            }, column));

            headers.push({
                value: 'actions',
                sortable: false
            }, {
                value: 'data-table-expand',
                sortable: false
            });

            return headers;
        },

        /**
         *
         * Return the available headers for show less of the data-record-edit-form
         *
         * @returns {*}
         */
        showLessEditHeaders() {
            //assign default values for the data-table itself (if needed)
            let headers = this.showLessColumns.map(column => _.merge( {
                align: _.get(column, 'align', 'left'),
                text: (_.get(column, 'show_short_title') ? _.get(column, 'short_title') : _.get(column, 'title')),
                title: _.get(column, 'title'),
                value: _.get(column, 'value', column.id),
                sortable: _.get(column, 'sortable', false),
            }, column));

            headers.push({
                value: 'actions',
                sortable: false
            }, {
                value: 'data-table-expand',
                sortable: false
            });

            return headers;
        },

        /**
         * Return the status of the bible
         *
         * @returns {*|null}
         */
        status() {
            return this.dataGathering ? this.dataGathering.status : null;
        },
    },

    actions: {
        setDataGathering(dataGathering){
            this.dataGathering = dataGathering;

            return this;
        },

        setDataRecords(dataRecords){
            if(typeof dataRecords === 'object'){
                this.dataRecords = dataRecords.items;
                this.patchPagination(dataRecords);
            }
            else if(Array.isArray(dataRecords)){
                this.dataRecords = dataRecords;
            }

            return this;
        },

        setCurrentDataRecord(dataRecord, currentIndex) {
            this.clearCurrentDataRecord();

            //a fresh instance that can be updated without changing any real attribute on the dataRecords set
            this.currentDataRecord = _.cloneDeep(dataRecord);
            const id = _.get(this.currentDataRecord, '_id');

            if(id){
                currentIndex = typeof currentIndex !== 'undefined'?
                    currentIndex :
                    this.dataRecords.findIndex(dataRecord => _.get(dataRecord, '_id') === id);

                if(currentIndex !== -1)
                    this.currentDataRecordIndex = currentIndex;
            }

            EventBus.$emit('data_gatherings.data-records.selected', this.currentDataRecord, this.currentDataRecordIndex);

            return this;
        },

        clearCurrentDataRecord() {
            this.currentDataRecord = {};
            this.currentDataRecordIndex = null;

            return this;
        },

        async fetchDataRecords() {
            return DataRecordsRepository.search(this.dataGathering, {
                conditions: this.filters,
                pagination: {
                    page: this.pagination.page,
                    max: this.pagination.max,
                },
                order: this.sortBy,
            }).then(response => this.setDataRecords(response.data));
        },

        async store(dataRecord) {

            return DataRecordsRepository
                .store(this.dataGathering, dataRecord)
                .then(async () => await this.fetchDataRecords());
        },

        async getRecuResume(dataRecord){
            return DataRecordsRepository
                .getRecuResume(this.dataGathering, dataRecord)
                .then(async () => await this.fetchDataRecords());
        },

        async update(dataRecord) {
            dataRecord = dataRecord || this.currentDataRecord;

            return DataRecordsRepository
                .update(this.dataGathering, dataRecord)
                .then(response => {
                    if(this.currentDataRecordIndex !== -1){
                        this.dataRecords[this.currentDataRecordIndex] = _.merge(
                            this.dataRecords[this.currentDataRecordIndex],
                            response.data
                        );
                    }
                });
        },

        async delete(dataRecord) {
            dataRecord = dataRecord || this.currentDataRecord;

            return DataRecordsRepository
                .delete(this.dataGathering, dataRecord)
                .then(async () => await this.fetchDataRecords());
        },

        applyFilters(filters) {
            this.filters = _.pickBy(filters, value => !!value);

            return this;
        },

        applySorting(dataTable) {
            this.sortBy = dataTable.options.sortBy
                .filter(column => !!column)
                .map((column, index) => [
                    column,
                    _.get(dataTable.options.sortDesc, index, false)? 'desc' : 'asc'
                ].join(','));

            return this;
        },

        setPagination(pagination){
            this.pagination = pagination;

            return this;
        },

        patchPagination(pagination){
            this.pagination.page = _.get(pagination, 'page', this.pagination.page);
            this.pagination.pages = _.get(pagination, 'pages', this.pagination.pages);
            this.pagination.max = _.get(pagination, 'max', this.pagination.max);
            this.pagination.total = _.get(pagination, 'total', this.pagination.total);

            if(this.pagination.page > this.pagination.pages){
                this.pagination.page = 1;
            }

            return this;
        },

        /**
         * Call to Data Records Repository
         * to fetch status of download
         * of a data-gathering
         * with dataGathering object
         *
         * @param dataGathering
         *
         * @returns {Promise<*>}
         */
        async fetchStatusDownload(dataGathering) {
             this.dataGathering = dataGathering;

            return DataRecordsRepository
                .statusDownload(this.dataGathering)
        },



        /**
         * Call to Data Records repository
         * to generate data-gathering Excel
         *
         * @param dataGathering
         *
         * @returns {Promise<*>}
         */
        async generate(dataGathering){
            this.dataGathering = dataGathering;

            return DataRecordsRepository
                .generate(this.dataGathering)
        },

        /**
         * Call to Data Records repository
         * and request to download
         * the data-gathering from gcs
         *
         * @param request
         *
         * @returns {Promise<*>}
         */
        async download(request){

            return DataRecordsRepository
                .download(request)
        }
    },
});
